import { cn } from '@my/ui';
import { AlertTriangle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PickOption } from '../hooks/usePlanResources';
import {
  validateDraft,
  type SubcontractorTrailerDraft,
  type TrailerPresence,
} from '../lib/subcontractorTrailers';

interface AddSubcontractorTrailerModalProps {
  open: boolean;
  subcontractorOptions: PickOption[];
  equipmentTypeOptions: PickOption[];
  /** Disables Save while the create request is in flight. */
  saving?: boolean;
  onCancel: () => void;
  onSave: (draft: SubcontractorTrailerDraft) => void;
}

/** What each presence option means, spelled out so the choice is self-explaining. */
const PRESENCE_OPTIONS: { value: TrailerPresence; title: string; blurb: string }[] = [
  {
    value: 'Drop',
    title: 'Drop trailer',
    blurb: "Parked for one job. Gone once assigned — doesn't come back.",
  },
  {
    value: 'Hire',
    title: 'Hire trailer',
    blurb: 'On hire for a period. Returns to this queue after each job.',
  },
];

const emptyDraft: SubcontractorTrailerDraft = {
  unitName: '',
  presence: 'Hire',
  onHireFrom: null,
  onHireTo: null,
  subcontractorPartyId: '',
  organisationEquipmentTypeId: null,
};

/**
 * Registers a subcontractor-supplied trailer.
 *
 * <p>
 * Follows the `ConfirmDialog` conventions — portalled to `document.body` so no
 * z-index parent can clip it, Escape and backdrop-click both dismiss — but it's
 * a form, not a yes/no, so it doesn't reuse that component.
 * </p>
 */
export function AddSubcontractorTrailerModal({
  open,
  subcontractorOptions,
  equipmentTypeOptions,
  saving = false,
  onCancel,
  onSave,
}: AddSubcontractorTrailerModalProps) {
  const [draft, setDraft] = useState<SubcontractorTrailerDraft>(emptyDraft);
  // Errors surface on submit, not per keystroke — flagging a field the planner
  // hasn't reached yet reads as the form being broken.
  const [submitted, setSubmitted] = useState(false);

  // Reset on open so a cancelled entry doesn't come back next time.
  useEffect(() => {
    if (!open) return;
    setDraft(emptyDraft);
    setSubmitted(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const errors = validateDraft(draft);
  const showError = (field: keyof typeof errors) => (submitted ? errors[field] : undefined);

  /**
   * The unit isn't in our fleet, so we can't confirm what it is or whose it is —
   * hence the required subcontractor. Shown once a name is typed rather than
   * immediately, so an untouched form isn't already complaining. It will stay up
   * permanently until subcontractor trailers have a table to be recognised
   * against.
   */
  const notRecognised = draft.unitName.trim().length > 0 && !draft.subcontractorPartyId;

  const submit = () => {
    setSubmitted(true);
    if (Object.keys(validateDraft(draft)).length > 0) return;
    onSave(draft);
  };

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-subcontractor-trailer-title"
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-md bg-white p-4 shadow-xl"
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <h3
            id="add-subcontractor-trailer-title"
            className="text-sm font-semibold text-slate-900"
          >
            Add subcontractor trailer
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="shrink-0 rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Unit name / reg" error={showError('unitName')}>
            <input
              type="text"
              autoFocus
              value={draft.unitName}
              onChange={(e) => setDraft((d) => ({ ...d, unitName: e.target.value }))}
              className={inputClass(!!showError('unitName'))}
            />
          </Field>

          <fieldset>
            <legend className="mb-1 text-xs font-medium text-slate-600">
              How is this unit here?
            </legend>
            {/* Two cards rather than a radio pair: the difference decides whether
                the unit survives its first job, which is worth the words. */}
            <div className="grid grid-cols-2 gap-2">
              {PRESENCE_OPTIONS.map((option) => {
                const active = draft.presence === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setDraft((d) => ({ ...d, presence: option.value }))}
                    className={cn(
                      'rounded-md border p-2 text-left transition-colors',
                      active
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-slate-300',
                    )}
                  >
                    <div className="text-xs font-semibold text-slate-800">{option.title}</div>
                    <div className="mt-0.5 text-[10px] leading-tight text-slate-500">
                      {option.blurb}
                    </div>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Only a hire has a period; a drop is here for one job and gone.
              The end date is optional — an open-ended hire is normal — but
              without it nothing ever marks the hire as lapsed. */}
          {draft.presence === 'Hire' && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="On hire from" error={showError('onHireFrom')}>
                <input
                  type="date"
                  value={draft.onHireFrom ?? ''}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, onHireFrom: e.target.value || null }))
                  }
                  className={inputClass(!!showError('onHireFrom'))}
                />
              </Field>
              <Field label="Until (optional)" error={showError('onHireTo')}>
                <input
                  type="date"
                  value={draft.onHireTo ?? ''}
                  min={draft.onHireFrom ?? undefined}
                  onChange={(e) => setDraft((d) => ({ ...d, onHireTo: e.target.value || null }))}
                  className={inputClass(!!showError('onHireTo'))}
                />
              </Field>
            </div>
          )}

          {notRecognised && (
            <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>Not recognised — select the subcontractor supplying this unit.</span>
            </div>
          )}

          <Field label="Subcontractor" error={showError('subcontractorPartyId')}>
            <select
              value={draft.subcontractorPartyId}
              onChange={(e) =>
                setDraft((d) => ({ ...d, subcontractorPartyId: e.target.value }))
              }
              className={inputClass(!!showError('subcontractorPartyId'))}
            >
              <option value="">Select a subcontractor…</option>
              {subcontractorOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Equipment type">
            <select
              value={draft.organisationEquipmentTypeId ?? ''}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  organisationEquipmentTypeId: e.target.value || null,
                }))
              }
              className={inputClass(false)}
            >
              <option value="">Not specified</option>
              {equipmentTypeOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded-sm bg-amber-500 px-6 py-1.5 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function inputClass(invalid: boolean) {
  return cn(
    'w-full rounded-md border px-2 py-1.5 text-sm text-slate-800 focus:outline-none',
    invalid
      ? 'border-red-300 focus:border-red-400'
      : 'border-slate-300 focus:border-amber-400',
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
      {error && <span className="mt-0.5 block text-[11px] text-red-600">{error}</span>}
    </label>
  );
}
